import React, { useState } from "react";
import { FaTrash, FaCirclePlus } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import ModalButton from "../../../../components/common/form/ModalButton";
import AddEditContactForm from "../../../../components/common/form/AddEditContactForm";
import { hasPermission } from "../../../../helpers/utils/AccessControlUtils";
import { handleRTKQuery } from "../../../../helpers/utils/RtkQueryUtils";
import {
    useCreateContactMutation,
    useDeleteContactsByIdMutation,
    useGetContactByRelationIdQuery,
    useUpdateContactMutation
} from "../../../../components/common/form/AddEditContactApi";
import { formatValueAsPhoneNumber } from "../../../../helpers/utils/Utils";
import ConfirmationModal from "../../../../components/layout/modal/ConfirmationModal";

const CollectorContactForm = () => {
    let { collectorId } = useParams();
    const { data: collectorContactData } = useGetContactByRelationIdQuery(collectorId);
    const iconButtonStyle = "cursor-pointer text-highlight-0";
    const [contactData, setContactData] = useState<any>({});
    const [show, setShow] = useState(false);
    const [updateContactApi, updateContactApiResponse] = useUpdateContactMutation();
    const [createContactApi, createContactApiResponse] = useCreateContactMutation();
    const [contactId, setContactId] = useState(null);
    const [deleteContacts, { isLoading }] = useDeleteContactsByIdMutation();

    const handleRowClick = (contactId: any) => {
        setContactId(contactId)
        setShow(true);
    };

    const handleSubmit = async (e: any) => {
        const isCreate = !contactId;

        const requestBody = {
            id: isCreate ? null : contactData?.id,
            name: contactData?.name,
            email: contactData?.email,
            website: contactData?.website,
            phone: contactData?.phone,
            fax: contactData?.fax,
            preferredContactMethod: contactData?.preferredContactMethod?.value || null,
            isPrimary: contactData?.isPrimary,
            note: contactData?.note,
            address: {
                ...contactData?.address,
                state: contactData?.address?.state?.value || null
            }
        };

        await handleRTKQuery(
            async () => {
                if (isCreate) {
                    return await createContactApi({
                        contactId: collectorId,
                        requestBody,
                        isSendLink: false,
                    }).unwrap();
                } else {
                    return await updateContactApi({
                        contactId,
                        requestBody,
                    }).unwrap();
                }
            },
            (isSuccess: boolean) => {
                if (isSuccess) {
                    setContactData({});
                    setShow(false);
                }
            }
        );
    };

    const handleDelete = async (contactId: string) => {
        if (contactId) {
            await deleteContacts(contactId).unwrap();
        }
    };

    return (
        <div className="bg-text-1 shadow-[0_3px_10px_#0003] text-sm mt-3 rounded-md">
            <div className="flex justify-between items-center border-b bordder border-surface-7 p-2">
                <h5 className="font-semibold text-surface-0">Collectors Contact
                    ({collectorContactData?.length || 0})</h5>
                {hasPermission(["COLL-U"]) &&
                    <ModalButton
                        triggerButton={<FaCirclePlus className={iconButtonStyle}
                            title={"Add Contact Form"} />}
                        modal={
                            <AddEditContactForm
                                formType={"Add"}
                                show={true}
                                setShow={setShow}
                                onSubmit={() => handleSubmit(contactData)}
                                setContactData={setContactData}
                                contactData={contactData}
                                isLoading={createContactApiResponse.isLoading}
                                overrideDismissMethod={() => {
                                    setContactData({});
                                }}
                                isReadOnly={hasPermission(["COLL-U"]) === false}
                            />
                        }
                    />}
            </div>
            <div className={'p-2 max-h-[150px] overflow-auto scrollbar-hide'}>
                <table className="w-full bg-text-0 border-2 border-[rgba(0,0,0,0.1)]">
                    <thead className="border-b-2 border-[rgba(0,0,0,0.1)]">
                        <tr>
                            {[
                                "Name",
                                "Phone",
                                "Email",
                                "Website",
                                "Address",
                                ...(hasPermission(["COLL-U"]) ? ["Action"] : [])
                            ].map((heading) => (
                                <th key={heading} className="p-2 text-left font-medium border-b border-surface-5 text-left whitespace-nowrap">{heading}</th>
                            ))}

                        </tr>
                    </thead>
                    <tbody>
                        {collectorContactData?.length === 0 ? (
                            <tr>
                                <td colSpan={3} className="text-center text-gray-500 p-4">No data available</td>
                            </tr>
                        ) : collectorContactData?.map((contact: any) => (
                            <tr key={contact.id} className="bg-text-1">
                                <td
                                    className="p-2  text-highlight-0 cursor-pointer text-highlight-4"
                                    onClick={() => handleRowClick(contact.id)}>
                                    {contact.name}
                                </td>
                                <td className="p-2">{formatValueAsPhoneNumber(contact?.phone)}</td>
                                <td className="px-4 py-2">
                                    <div className="fa_icon_btn_center_align">
                                        <span className={contact?.email !== null ? "fa fa-envelope" : ""}
                                            title={contact?.email !== null ? contact?.email : ""} /></div>
                                </td>
                                <td className="px-4 py-2">
                                    <div className="fa_icon_btn_center_align">
                                        {contact.website !== null && (
                                            <a
                                                href={contact.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={contact.website}
                                            >
                                                <span className="fa fa-globe text-highlight-0" />
                                            </a>
                                        )}
                                    </div>
                                </td>

                                <td className="p-2">{contact.address?.mailingAddress || "-"}</td>
                                <td className="px-4 py-2">
                                    {hasPermission(["COLL-U"]) === true &&
                                        <ModalButton
                                            triggerButton={<FaTrash className={iconButtonStyle}
                                                title={"Delete Contact"} />}
                                            modal={
                                                <ConfirmationModal
                                                    show={true}
                                                    setShow={() => {
                                                    }}
                                                    title={"Delete Contact"}
                                                    bodyMessage={"Are you sure you want to delete this contact?"}
                                                    onSubmit={() => handleDelete(contact.id)}
                                                    isLoading={isLoading}
                                                />
                                            }
                                        />}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {show && (
                    <AddEditContactForm
                        formType={"Update"}
                        show={true}
                        setShow={setShow}
                        contactId={contactId}
                        setContactData={setContactData}
                        contactData={contactData}
                        onSubmit={() => handleSubmit(contactData)}
                        isLoading={updateContactApiResponse.isLoading}
                        overrideDismissMethod={() => {
                            setContactId(null);
                            setContactData({});
                        }}
                        isReadOnly={hasPermission(["COLL-U"]) === false}
                    />
                )}
            </div>

        </div>
    );
};

export default CollectorContactForm;